import { useLocation } from 'react-router-dom';
import {
    Box, Container, Typography, Chip, Avatar, Button, Card, CardContent, Rating, Divider, CircularProgress
} from '@mui/material';
import { useEffect, useState } from 'react';

import useGetRequest from '../../Hooks/useGetRequest';
import resolveImageUrl from '../../utils/resolveImageUrl';
import { brandGradient } from '../../shared-theme/themePrimitives';
import { SENTIMENT_COLORS, sentimentLabel } from '../../utils/sentiment';

const CreatorDetail = () => {
    const location = useLocation();
    const { get, loading } = useGetRequest();
    const [user, setUser] = useState();
    useEffect(() => {
        const fetchData = async () => {
            const response = await get(`creator-profile/+${location.state?.creator.id}`);
            setUser(response.data);
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography variant="h6" color="text.secondary">Creator not found</Typography>
            </Box>
        );
    }

    const avatarUrl = resolveImageUrl(user.creator.profile_image);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Cover + identity */}
            <Box sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 4 }}>
                <Box sx={{
                    height: { xs: 100, sm: 140 },
                    background: avatarUrl
                        ? `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.35)), url(${avatarUrl})`
                        : brandGradient,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }} />
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'flex-end' },
                    gap: 2,
                    px: 3,
                    pb: 3,
                    mt: '-48px',
                    textAlign: { xs: 'center', sm: 'left' },
                }}>
                    <Avatar
                        src={avatarUrl || undefined}
                        sx={{
                            width: 96,
                            height: 96,
                            border: '4px solid',
                            borderColor: 'background.paper',
                            boxShadow: 2,
                            fontSize: '2rem',
                            fontWeight: 700,
                            bgcolor: 'secondary.main',
                        }}
                    >
                        {user.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" fontWeight={700}>{user.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {user.creator.content_category?.join(' • ')}
                        </Typography>
                        {user.creator.rating != null && (
                            <Rating value={user.creator.rating} precision={0.5} readOnly size="small" sx={{ mt: 0.5 }} />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {user.creator.top_creator === 1 && (
                            <Chip label="Top Creator" color="secondary" />
                        )}
                        {user.creator.fast_response === 1 && (
                            <Chip label="Fast Response" color="success" />
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4,
            }}>
                {/* Left column */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        {user.creator.description}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
                        {user.creator.content_category?.map((cat, idx) => (
                            <Chip key={idx} label={cat} variant="outlined" />
                        ))}
                        {user.creator.gender && <Chip label={user.creator.gender} variant="outlined" />}
                    </Box>

                    {/* Platforms */}
                    {user.platforms?.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {user.platforms.map((platform, index) => (
                                <Card
                                    key={index}
                                    variant="outlined"
                                    sx={{
                                        flex: '1 1 260px',
                                        maxWidth: 320,
                                        borderRadius: 3,
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
                                            <Avatar src={platform.thumbnail} sx={{ width: 44, height: 44 }} />
                                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={700} noWrap>
                                                    {platform.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                                    {platform.genre?.join(', ')}
                                                </Typography>
                                            </Box>
                                            {sentimentLabel(platform.sentiment_label) && (
                                                <Chip
                                                    label={'Sentiment:' + sentimentLabel(platform.sentiment_label)}
                                                    size="small"
                                                    color={SENTIMENT_COLORS[platform.sentiment_label] || 'default'}
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                            <Typography variant="body2" textAlign="center">
                                                <Box component="span" fontWeight={700} color="secondary.main">
                                                    {platform.followers_count}
                                                </Box><br />
                                                Followers
                                            </Typography>
                                            <Typography variant="body2" textAlign="center">
                                                <Box component="span" fontWeight={700} color="secondary.main">
                                                    {platform.likes_count}
                                                </Box><br />
                                                {platform.name === 'YouTube' ? 'Views' : 'Likes'}
                                            </Typography>
                                            <Typography variant="body2" textAlign="center">
                                                <Box component="span" fontWeight={700} color="secondary.main">
                                                    {platform.posts_count}
                                                </Box><br />
                                                Posts
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                                            {[
                                                ['Reel', platform.reel_price],
                                                ['Story', platform.story_price],
                                                ['Live', platform.live_price],
                                            ].map(([label, price]) => (
                                                <Box key={label} sx={{
                                                    p: 1,
                                                    borderRadius: 2,
                                                    textAlign: 'center',
                                                    bgcolor: 'action.hover',
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                    <Typography variant="body2" fontWeight={700}>${price}</Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            href={platform.profile_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ borderRadius: 2 }}
                                        >
                                            View Profile
                                        </Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Contact card */}
                <Card
                    variant="outlined"
                    sx={{
                        width: { xs: '100%', md: 340 },
                        flexShrink: 0,
                        borderRadius: 3,
                        alignSelf: 'flex-start',
                        position: { md: 'sticky' },
                        top: { md: 88 },
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                            Contact Info
                        </Typography>

                        <Box sx={{
                            bgcolor: 'action.hover',
                            p: 2,
                            mb: 2.5,
                            borderRadius: 2,
                            borderLeft: '4px solid',
                            borderColor: 'secondary.main',
                        }}>
                            <Typography variant="body2" color="text.secondary">
                                I will create high-quality original content featuring your brand's products with an engaging caption.
                            </Typography>
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ borderRadius: 2, py: 1.2 }}
                        >
                            Contact
                        </Button>

                        <Typography
                            variant="caption"
                            display="block"
                            align="center"
                            sx={{
                                mt: 2,
                                color: 'text.secondary',
                                '& a': {
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    fontWeight: 'medium',
                                    '&:hover': { textDecoration: 'underline' },
                                },
                            }}
                        >
                            Can't find what you need? <a href="#custom">Request Custom Offer</a>
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};

export default CreatorDetail;
